import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import api from "@/api/v1-jwt/config/api";
import { Button } from "@/components/ui/button-tw";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/utils/classnames";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  useCreateClientSettings,
  SettingKey,
  SettingValueType,
  SettingCategory,
} from "@/queries/client-settings";

interface Branding {
  logo: string;
  brandColor: string;
  secondaryColor: string;
}

interface BrandingSectionProps {
  userId: string;
  isAdmin: boolean;
  initialData: Branding | null;
  onUpdate: (data: Branding) => void;
  onLogoPreviewUpdate: (preview: string) => void;
}

const isColorTooLight = (color: string): boolean => {
  const hex = color.replace("#", "");
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return false;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return brightness > 0.8;
};

export const BrandingSection = ({
  userId,
  isAdmin,
  initialData,
  onUpdate,
  onLogoPreviewUpdate,
}: BrandingSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [branding, setBranding] = useState<Branding>({
    logo: "",
    brandColor: "#000000",
    secondaryColor: "#000000",
  });
  const [originalBranding, setOriginalBranding] = useState<Branding>({
    logo: "",
    brandColor: "#000000",
    secondaryColor: "#000000",
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");

  const createClientSettingsMutation = useCreateClientSettings({
    onSuccess: () => {
      toast.success("Branding settings saved successfully!");
    },
    onError: (error) => {
      toast.error("Failed to save branding settings. Please try again.");
      console.error("Error saving branding settings:", error);
    },
  });

  useEffect(() => {
    if (initialData) {
      setBranding(initialData);
      setOriginalBranding(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    onUpdate(branding);
  }, [branding, onUpdate]);

  useEffect(() => {
    onLogoPreviewUpdate(logoPreview);
  }, [logoPreview, onLogoPreviewUpdate]);

  const normalizeHexColor = (color: string): string => {
    if (!color) return "#000000";

    let hex = color.replace("#", "");

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
      return "#000000";
    }

    return "#" + hex;
  };

  const getLogoUrl = (logoPath: string): string => {
    if (!logoPath) return "";
    if (logoPath.startsWith("http://") || logoPath.startsWith("https://")) {
      return logoPath;
    }
    return `https://cdn.pocketbook.tech/${logoPath}`;
  };

  const updateBranding = (field: string, value: string) => {
    let normalizedValue = value;

    if (field === "brandColor" || field === "secondaryColor") {
      normalizedValue = normalizeHexColor(value);
    }

    setBranding((prev) => ({
      ...prev,
      [field]: normalizedValue,
    }));
  };

  const uploadLogoForInvoice = async (file: File): Promise<string> => {
    if (!userId) {
      throw new Error("User ID is required for logo upload");
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post(
        `/settings/files/${userId}/upload/client_settings`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      return data.data;
    } catch (error) {
      console.error("Error uploading logo:", error);
      throw new Error("Failed to upload logo");
    }
  };

  const handleSave = async () => {
    if (!userId) return;

    if (isColorTooLight(branding.brandColor)) {
      toast.error(
        "Primary color needs higher contrast. Please choose a darker color.",
      );
      return;
    }
    if (isColorTooLight(branding.secondaryColor)) {
      toast.error(
        "Secondary color needs higher contrast. Please choose a darker color.",
      );
      return;
    }

    const settings: Array<{
      user_id: string;
      key: SettingKey;
      category: SettingCategory;
      value: any;
      value_type: SettingValueType;
      description: string;
      active: boolean;
    }> = [];

    let uploadedLogoUrl: string | undefined;

    if (logoFile) {
      try {
        uploadedLogoUrl = await uploadLogoForInvoice(logoFile);
        settings.push({
          user_id: userId,
          key: SettingKey.LOGO_URL,
          category: SettingCategory.BRANDING,
          value: { logo_url: uploadedLogoUrl },
          value_type: SettingValueType.STRING,
          description: "Company logo for invoices",
          active: true,
        });
      } catch (error) {
        toast.error("Failed to upload logo. Please try again.");
        return;
      }
    }

    if (branding.brandColor !== originalBranding.brandColor) {
      settings.push({
        user_id: userId,
        key: SettingKey.PRIMARY_COLOR,
        category: SettingCategory.BRANDING,
        value: { primary_color: branding.brandColor },
        value_type: SettingValueType.STRING,
        description: "Primary brand color for invoices",
        active: true,
      });
    }
    if (branding.secondaryColor !== originalBranding.secondaryColor) {
      settings.push({
        user_id: userId,
        key: SettingKey.SECONDARY_COLOR,
        category: SettingCategory.BRANDING,
        value: { secondary_color: branding.secondaryColor },
        value_type: SettingValueType.STRING,
        description: "Secondary brand color for invoices",
        active: true,
      });
    }

    if (settings.length === 0) return;

    createClientSettingsMutation.mutate(
      { settings },
      {
        onSuccess: () => {
          if (logoFile && uploadedLogoUrl) {
            setOriginalBranding({
              ...branding,
              logo: uploadedLogoUrl,
            });
            setBranding((prev) => ({
              ...prev,
              logo: uploadedLogoUrl,
            }));
          } else {
            setOriginalBranding(branding);
          }
          setLogoFile(null);
          setLogoPreview("");
        },
      },
    );
  };

  const handleCancel = () => {
    setBranding(originalBranding);
    setLogoFile(null);
    setLogoPreview("");
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-white rounded-lg shadow">
        <CollapsibleTrigger className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <h3 className="text-lg font-semibold text-gray-900">Branding</h3>
          <ChevronDown
            className={cn(
              "h-5 w-5 text-gray-500 transition-transform",
              isOpen && "transform rotate-180",
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-6 pb-6 space-y-4 border-t border-gray-100">
            <div className="pt-4">
              <Label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </Label>
              <div className="flex items-center gap-4">
                <div
                  className="w-24 h-24 border-2 border-gray-300 rounded-lg flex items-center justify-center overflow-hidden p-2"
                  style={{ backgroundColor: branding.brandColor }}
                >
                  {logoPreview || branding.logo ? (
                    <img
                      src={logoPreview || getLogoUrl(branding.logo)}
                      alt="Company logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-gray-400">No logo</span>
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/png,image/jpeg,image/jpg"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setLogoFile(file);

                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const result = reader.result as string;
                          setLogoPreview(result);
                        };
                        reader.readAsDataURL(file);
                        e.target.value = "";
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      document.getElementById("logo-upload")?.click()
                    }
                    disabled={isAdmin}
                  >
                    Upload Logo
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG or JPG (max. 2MB)
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Brand Color
                </Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    className="w-12 h-10 rounded border border-gray-300"
                    value={branding.brandColor}
                    onChange={(e) =>
                      updateBranding("brandColor", e.target.value)
                    }
                    disabled={isAdmin}
                  />
                  <Input
                    type="text"
                    placeholder="#000000"
                    className="flex-1"
                    value={branding.brandColor}
                    onChange={(e) => {
                      setBranding((prev) => ({
                        ...prev,
                        brandColor: e.target.value,
                      }));
                    }}
                    onBlur={(e) => {
                      updateBranding("brandColor", e.target.value);
                    }}
                    disabled={isAdmin}
                  />
                </div>
                {isColorTooLight(branding.brandColor) && (
                  <p className="text-xs text-red-500 mt-1">
                    Color is too light to display invoice information. Please
                    choose a darker color.
                  </p>
                )}
              </div>

              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary Brand Color
                </Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    className="w-12 h-10 rounded border border-gray-300"
                    value={branding.secondaryColor}
                    onChange={(e) =>
                      updateBranding("secondaryColor", e.target.value)
                    }
                    disabled={isAdmin}
                  />
                  <Input
                    type="text"
                    placeholder="#000000"
                    className="flex-1"
                    value={branding.secondaryColor}
                    onChange={(e) => {
                      setBranding((prev) => ({
                        ...prev,
                        secondaryColor: e.target.value,
                      }));
                    }}
                    onBlur={(e) => {
                      updateBranding("secondaryColor", e.target.value);
                    }}
                    disabled={isAdmin}
                  />
                </div>
                {isColorTooLight(branding.secondaryColor) && (
                  <p className="text-xs text-red-500 mt-1">
                    Color is too light to display invoice information. Please
                    choose a darker color.
                  </p>
                )}
              </div>
            </div>

            {!isAdmin && (
              <div className="flex justify-end gap-3 pt-4 ">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-600/80 text-white"
                  onClick={handleSave}
                  disabled={
                    createClientSettingsMutation.isPending ||
                    isColorTooLight(branding.brandColor) ||
                    isColorTooLight(branding.secondaryColor)
                  }
                >
                  {createClientSettingsMutation.isPending
                    ? "Saving..."
                    : "Save Changes"}
                </Button>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
